import { useEffect, useMemo, useState } from 'react'
import { supabase } from '@/lib/supabase'
import Button from '@/components/common/Button'
import { toast } from 'sonner'

const SPORT_OPTIONS = [
  { value: 'hockey', label: 'Hockey' }
]

const HOCKEY_LEVELS = [
  { value: 'boys_mite_8u', label: 'Boys Mite (8U)' },
  { value: 'boys_squirt_10u', label: 'Boys Squirt (10U)' },
  { value: 'boys_peewee_12u', label: 'Boys Peewee (12U)' },
  { value: 'boys_bantam_14u', label: 'Boys Bantam (14U)' },
  { value: 'girls_8u', label: 'Girls 8U' },
  { value: 'girls_10u', label: 'Girls 10U' },
  { value: 'girls_12u', label: 'Girls 12U' },
  { value: 'girls_15u', label: 'Girls 15U' }
]

const LEVEL_OPTIONS = {
  hockey: HOCKEY_LEVELS
}

const LEVEL_DETAIL_OPTIONS = ['','AAA','AA','A','B1','B2','B','C','Tier I','Tier II','House','Travel']

const LEVEL_ALIAS_MAP = new Map()

function sanitize(value = '') {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '')
}

Object.entries(LEVEL_OPTIONS).forEach(([sport, levels]) => {
  levels.forEach((entry) => {
    const aliases = new Set([
      entry.value,
      entry.value.replace(/_/g, ' '),
      entry.value.replace(/_/g, ''),
      entry.label,
      entry.label.replace(/[^a-z0-9]/gi, ''),
      entry.label.replace(/\(.*\)/, '').trim()
    ])

    aliases.forEach((alias) => {
      const key = sanitize(alias)
      if (key) {
        LEVEL_ALIAS_MAP.set(`${sport}:${key}`, entry.value)
      }
    })
  })
})

function normalizeTeamLevel(level, sport) {
  const key = sanitize(level)
  if (!key) return null
  return LEVEL_ALIAS_MAP.get(`${sport}:${key}`) || LEVEL_ALIAS_MAP.get(`hockey:${key}`) || null
}

export default function SeedTeamEditModal({ team, onClose, onSave }) {
  const [formData, setFormData] = useState({
    name: team.name,
    association_name: team.association_name || '',
    sport_type: team.sport_type || 'hockey',
    team_level: team.team_level,
    level_detail: team.level_detail || '',
    city: team.city,
    state: team.state,
    home_rink_name: team.home_rink_name || '',
    home_rink_alt: team.home_rink_alt || '',
    calendar_sync_url: team.calendar_sync_url || '',
    calendar_sync_type: team.calendar_sync_type || '',
    calendar_sync_enabled: team.calendar_sync_enabled,
    admin_notes: team.admin_notes || ''
  })
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    const options = LEVEL_OPTIONS[formData.sport_type] || []
    if (options.length > 0 && !options.some((opt) => opt.value === formData.team_level)) {
      setFormData((prev) => ({
        ...prev,
        team_level: options[0].value
      }))
    }
  }, [formData.sport_type, formData.team_level])

  const levelOptions = useMemo(() => LEVEL_OPTIONS[formData.sport_type] || [], [formData.sport_type])

  async function handleSave() {
    try {
      setSaving(true)

      const normalizedLevel = normalizeTeamLevel(formData.team_level, formData.sport_type)
      if (!normalizedLevel) {
        throw new Error('Unsupported team level value')
      }

      const { error } = await supabase
        .from('teams')
        .update({
          ...formData,
          name: formData.name.trim(),
          association_name: formData.association_name.trim(),
          team_level: normalizedLevel,
          level_detail: formData.level_detail ? formData.level_detail.toUpperCase() : null,
          city: formData.city.trim(),
          state: formData.state.trim(),
          home_rink_name: formData.home_rink_name.trim(),
          home_rink_alt: formData.home_rink_alt.trim(),
          calendar_sync_url: formData.calendar_sync_url.trim(),
          calendar_sync_type: formData.calendar_sync_type || null,
          admin_notes: formData.admin_notes.trim()
        })
        .eq('id', team.id)

      if (error) throw error

      toast.success('Team updated successfully')
      onSave()
    } catch (error) {
      console.error('Error updating team:', error)
      toast.error(`Failed to update team: ${error.message}`)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Edit Seed Team</h2>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Team Name *
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Association
              </label>
              <input
                type="text"
                value={formData.association_name}
                onChange={(e) => setFormData({ ...formData, association_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Sport *
              </label>
              <select
                value={formData.sport_type}
                onChange={(e) => setFormData({ ...formData, sport_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {SPORT_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Team Level *
              </label>
              <select
                value={formData.team_level}
                onChange={(e) => setFormData({ ...formData, team_level: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {levelOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Level Detail
              </label>
              <select
                value={formData.level_detail}
                onChange={(e) => setFormData({ ...formData, level_detail: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                {LEVEL_DETAIL_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option === '' ? 'Select (optional)' : option}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City *
              </label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State *
              </label>
              <input
                type="text"
                value={formData.state}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="MN"
                required
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Home Rink
              </label>
              <input
                type="text"
                value={formData.home_rink_name}
                onChange={(e) => setFormData({ ...formData, home_rink_name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Secondary Rink
              </label>
              <input
                type="text"
                value={formData.home_rink_alt}
                onChange={(e) => setFormData({ ...formData, home_rink_alt: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Calendar URL *
              </label>
              <input
                type="url"
                value={formData.calendar_sync_url}
                onChange={(e) => setFormData({ ...formData, calendar_sync_url: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="https://..."
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Calendar Type
              </label>
              <select
                value={formData.calendar_sync_type}
                onChange={(e) => setFormData({ ...formData, calendar_sync_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Auto-detect</option>
                <option value="ical">iCal</option>
                <option value="html">HTML</option>
                <option value="webcal">WebCal</option>
              </select>
            </div>

            <div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={formData.calendar_sync_enabled}
                  onChange={(e) => setFormData({ ...formData, calendar_sync_enabled: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">
                  Enable Calendar Sync
                </span>
              </label>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Admin Notes
              </label>
              <textarea
                value={formData.admin_notes}
                onChange={(e) => setFormData({ ...formData, admin_notes: e.target.value })}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Internal notes about this team..."
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  )
}

